/**
 * Outlook sync (`plans/outlook-sync.md`): the viewer's Microsoft account, the
 * folders of their own mailbox, and the folders that sync into a project. The
 * emails themselves arrive through the import, and are listed with the
 * project's other documents.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  MailFolders,
  MicrosoftSignInStatus,
  MySyncedFolder,
  OutlookPreview,
  OutlookStatus,
} from "@gb/contracts";
import { api, errorMessage } from "@/api/client";
import { documentKeys, importKeys } from "./documents-api";
import { runMicrosoftPopup } from "./microsoft-popup";

export const outlookKeys = {
  account: ["connectors", "microsoft"] as const,
  mailFolders: ["connectors", "microsoft", "mail-folders"] as const,
  mine: ["connectors", "microsoft", "synced-folders"] as const,
  status: (projectId: string) => ["outlook", projectId] as const,
};

/**
 * How often a project's folders are read again. The worker polls them once a
 * minute, so every 30 seconds shows each poll within half a minute of it, and
 * quicker while emails are waiting, so they are seen arriving.
 */
const READ_EVERY_MS = 30_000;
const READ_WHILE_WAITING_MS = 5_000;
const READ_WHILE_CHECKING_MS = 2_000;

export function useMicrosoftAccount() {
  return useQuery({
    queryKey: outlookKeys.account,
    queryFn: async (): Promise<MicrosoftSignInStatus> => {
      const res = await api.outlook.microsoftStatus();
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body;
    },
  });
}

export function useOutlookStatus(projectId: string) {
  return useQuery({
    queryKey: outlookKeys.status(projectId),
    queryFn: async (): Promise<OutlookStatus> => {
      const res = await api.outlook.status({ params: { id: projectId } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body;
    },
    refetchInterval: (query) => {
      const folders = query.state.data?.folders ?? [];
      if (folders.length === 0) return false;
      // A poll is on its way: its answer is shown as soon as it is in.
      if (query.state.data?.checking) return READ_WHILE_CHECKING_MS;
      return folders.some((folder) => folder.waiting > 0 || folder.health === "waiting")
        ? READ_WHILE_WAITING_MS
        : READ_EVERY_MS;
    },
  });
}

/** The folders of the viewer's own mailbox, for the picker. Read only while the picker is open. */
export function useMailFolders(enabled: boolean) {
  return useQuery({
    queryKey: outlookKeys.mailFolders,
    queryFn: async (): Promise<MailFolders> => {
      const res = await api.outlook.mailFolders();
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body;
    },
    enabled,
    // The mailbox changes while the picker is open less often than it reopens.
    staleTime: 30_000,
  });
}

/** Every folder of the viewer's mailbox that syncs into a project (question 27). */
export function useMySyncedFolders(enabled = true) {
  return useQuery({
    queryKey: outlookKeys.mine,
    queryFn: async (): Promise<MySyncedFolder[]> => {
      const res = await api.outlook.mySyncedFolders();
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.folders;
    },
    enabled,
  });
}

/** What follows a change of folders: the status, the lists, and the emails and imports they bring. */
function useRefresh(projectId?: string) {
  const queryClient = useQueryClient();
  return (status?: OutlookStatus) => {
    if (status && projectId) queryClient.setQueryData(outlookKeys.status(projectId), status);
    void queryClient.invalidateQueries({ queryKey: ["outlook"] });
    void queryClient.invalidateQueries({ queryKey: outlookKeys.mailFolders });
    void queryClient.invalidateQueries({ queryKey: outlookKeys.mine });
    if (projectId) {
      void queryClient.invalidateQueries({ queryKey: documentKeys.list(projectId) });
      void queryClient.invalidateQueries({ queryKey: importKeys.list(projectId) });
    }
  };
}

/**
 * "Confirm with Microsoft" (question 3): Microsoft's sign-in in a popup, which
 * asks who the person is and nothing else, then the API keeps who they are.
 * A popup, as Google Drive's, so the screen behind keeps its state.
 */
export function useConfirmMicrosoft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<MicrosoftSignInStatus> => {
      const urlRes = await api.outlook.microsoftAuthUrl();
      if (urlRes.status !== 200) throw new Error(errorMessage(urlRes));
      const { code, state } = await runMicrosoftPopup(urlRes.body.url);
      const res = await api.outlook.microsoftCallback({ body: { code, state } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body;
    },
    onSuccess: (status) => {
      queryClient.setQueryData(outlookKeys.account, status);
      void queryClient.invalidateQueries({ queryKey: ["connectors", "microsoft"] });
      void queryClient.invalidateQueries({ queryKey: ["outlook"] });
    },
  });
}

/** Forget the viewer's account: every folder of it stops syncing, and the emails stay. */
export function useForgetMicrosoft() {
  const refresh = useRefresh();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<void> => {
      const res = await api.outlook.microsoftForget();
      if (res.status !== 204) throw new Error(errorMessage(res));
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: outlookKeys.account });
      refresh();
    },
  });
}

/** Check the chosen folders and count their emails. Syncs nothing. */
export function usePreviewOutlookFolders(projectId: string) {
  return useMutation({
    mutationFn: async (folderIds: string[]): Promise<OutlookPreview> => {
      const res = await api.outlook.preview({ params: { id: projectId }, body: { folderIds } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body;
    },
  });
}

export function useChooseOutlookFolders(projectId: string) {
  const refresh = useRefresh(projectId);
  return useMutation({
    mutationFn: async (folderIds: string[]): Promise<OutlookStatus> => {
      const res = await api.outlook.choose({ params: { id: projectId }, body: { folderIds } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body;
    },
    onSuccess: refresh,
  });
}

/** Stop one folder syncing into a project. Its emails stay. */
export function useStopOutlookFolder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ projectId, linkId }: { projectId: string; linkId: string }): Promise<string> => {
      const res = await api.outlook.stop({ params: { id: projectId, linkId } });
      if (res.status !== 204) throw new Error(errorMessage(res));
      return projectId;
    },
    onSuccess: (projectId) => {
      void queryClient.invalidateQueries({ queryKey: outlookKeys.status(projectId) });
      void queryClient.invalidateQueries({ queryKey: outlookKeys.mailFolders });
      void queryClient.invalidateQueries({ queryKey: outlookKeys.mine });
      void queryClient.invalidateQueries({ queryKey: importKeys.list(projectId) });
    },
  });
}

export function useSyncOutlookNow(projectId: string) {
  const refresh = useRefresh(projectId);
  return useMutation({
    mutationFn: async (): Promise<OutlookStatus> => {
      const res = await api.outlook.syncNow({ params: { id: projectId }, body: {} });
      if (res.status !== 202) throw new Error(errorMessage(res));
      return res.body;
    },
    onSuccess: refresh,
  });
}
